import React from 'react';
import { withScope, captureException } from '@sentry/react';
import { Button } from '@ordr-as/umami-react/button';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { ErrorMessage } from '../../generated-api';
import { ApiError } from '../api';
import { NetworkError } from '../utils/error-handler';

const dateFormatter = Intl.DateTimeFormat('nb-NO', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

interface ErrorBoundaryProps {
  children: React.ReactNode;
  title?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorTitle?: string;
  errorDescription?: string;
  timestamp?: string;
  traceId?: string;
  errorMessage?: string;
}

// description
// timestamp
// traceId

const defaultState: ErrorBoundaryState = {
  hasError: false,
  errorTitle: 'Unknown Error',
  errorDescription: '',
  timestamp: undefined,
  traceId: '',
  errorMessage: 'Something went wrong, please take a screenshot and send it to Support',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const instanceOfErrorMessage = (object: any): object is ErrorMessage => {
  return 'statusCode' in object;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { ...defaultState, errorTitle: props.title };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(_error: ErrorMessage | Error): ErrorBoundaryState {
    if (instanceOfErrorMessage(_error)) {
      return {
        hasError: true,
        errorDescription: _error.message ? _error.message : _error.description,
        timestamp: _error.timestamp
          ? dateFormatter.format(new Date(Number(_error.timestamp) * 1000))
          : dateFormatter.format(new Date(Date.now())),
        traceId: _error.traceId ? _error.traceId : defaultState.traceId,
      };
    }

    if (_error instanceof ApiError) {
      return {
        hasError: true,
        errorTitle: _error.title ?? `API _error (${_error.response.status})`,
        errorDescription: _error.message,
        timestamp: dateFormatter.format(new Date()),
        traceId: _error.traceId || '',
      };
    }

    if (_error instanceof NetworkError) {
      return {
        hasError: true,
        errorTitle: 'Network Error',
        errorDescription: _error.message,
        timestamp: dateFormatter.format(new Date()),
      };
    }

    return {
      hasError: true,
      errorTitle: defaultState.errorTitle,
      errorDescription: _error.message ? _error.message?.toString() : defaultState.errorMessage,
      timestamp: dateFormatter.format(new Date(Date.now())),
    };
  }

  componentDidCatch(error: Error | ErrorMessage, errorInfo: React.ErrorInfo) {
    if (!instanceOfErrorMessage(error)) {
      withScope((scope) => {
        scope.setTag('errorType', error.name);
        scope.setExtra('ErrorInfo', JSON.stringify(errorInfo));

        if (error instanceof ApiError) {
          scope.setExtra('errorBody', error.errorBody);
          scope.setExtra('responseStatus', error.response.status);
          scope.setContext('ResponseHeaders', Object.fromEntries(error.response.headers.entries()));
        }

        if ('originalError' in error) {
          scope.setContext('OriginalError', (error as any).originalError);
        }

        captureException(error);
      });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Dialog
          className="w-fit max-w-[50%]"
          title={this.state.errorTitle ? this.state.errorTitle : 'Something went wrong'}
          onClose={() => this.setState(defaultState)}
        >
          <div className="flex flex-col">
            <div>
              <p>{this.state.errorDescription}</p>
              <div className="text-grey-dark mt-6">
                {this.state.timestamp && <p className=" self-start text-sm mb-2">Timestamp : {this.state.timestamp}</p>}
                {this.state.traceId && <p className=" self-start text-sm">TraceId : {this.state.traceId}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button className="button secondary" kind="secondary" onClick={() => this.setState(defaultState)}>
                Close
              </Button>
            </div>
          </div>
        </Dialog>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
